import React from 'react';
import { ImageIcon, Video, Play, X, Film } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const MediaAttachments = ({ mediaUrls, onRemove, onClearAll, onAddVideo }) => {
  if (mediaUrls.length === 0) return null;

  return (
    <Card className="border-slate-100" data-testid="media-attachments-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            Media Attachments
            <Badge variant="secondary" className="text-xs">
              {mediaUrls.length}
            </Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={onClearAll}
          >
            Clear All
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {mediaUrls.map((url, index) => {
            const isVideo = url.includes('/video/') || url.includes('.mp4') || url.includes('.mov');
            const videoId = isVideo ? url.split('/').pop()?.replace('.mp4', '') : null;
            
            return (
              <div
                key={`media-${url.slice(-12)}-${index}`}
                className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
                data-testid={`media-attachment-${index}`}
              >
                {isVideo ? (
                  <div className="aspect-video relative">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/video/thumbnail/${videoId}?token=${localStorage.getItem('token')}`}
                      alt={`Video ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="absolute inset-0 bg-slate-800 items-center justify-center hidden">
                      <Video className="w-8 h-8 text-slate-400" />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-5 h-5 text-slate-800 ml-0.5" />
                      </div>
                    </div>
                    <Badge className="absolute top-2 left-2 bg-purple-600 text-white text-xs">
                      Video
                    </Badge>
                  </div>
                ) : (
                  <div className="aspect-video relative">
                    <img
                      src={url}
                      alt={`Media ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Badge className="absolute top-2 left-2 bg-blue-600 text-white text-xs">
                      Image
                    </Badge>
                  </div>
                )}
                <button
                  onClick={() => {
                    onRemove(index);
                    toast.success('Media removed');
                  }}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  title="Remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
          <button
            onClick={onAddVideo}
            className="aspect-video rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-emerald-600"
          >
            <Film className="w-6 h-6" />
            <span className="text-xs font-medium">Add Video</span>
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MediaAttachments;
